<script setup lang="ts">
import { onBeforeUnmount, ref } from 'vue'

interface Props {
  content?: string
  placement?: 'top' | 'bottom' | 'left' | 'right'
  delay?: number
}

withDefaults(defineProps<Props>(), {
  placement: 'top',
  delay: 150,
})

const visible = ref(false)
let showTimer: number | null = null
let hideTimer: number | null = null

function show(delay: number): void {
  if (hideTimer !== null) {
    window.clearTimeout(hideTimer)
    hideTimer = null
  }
  showTimer = window.setTimeout(() => {
    visible.value = true
  }, delay)
}

function hide(): void {
  if (showTimer !== null) {
    window.clearTimeout(showTimer)
    showTimer = null
  }
  hideTimer = window.setTimeout(() => {
    visible.value = false
  }, 80)
}

onBeforeUnmount(() => {
  if (showTimer !== null) window.clearTimeout(showTimer)
  if (hideTimer !== null) window.clearTimeout(hideTimer)
})

const placementClass: Record<NonNullable<Props['placement']>, string> = {
  top:    'bottom-full left-1/2 -translate-x-1/2 mb-2',
  bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
  left:   'right-full top-1/2 -translate-y-1/2 mr-2',
  right:  'left-full top-1/2 -translate-y-1/2 ml-2',
}
</script>

<template>
  <span
    class="relative inline-flex"
    @mouseenter="show(delay)"
    @mouseleave="hide"
    @focusin="show(delay)"
    @focusout="hide"
  >
    <slot />
    <transition name="tooltip">
      <span
        v-if="visible"
        class="pointer-events-none absolute z-30 whitespace-nowrap rounded-md bg-fg px-2 py-1 text-xs font-medium text-fg-inverted shadow-md"
        :class="placementClass[placement]"
        role="tooltip"
      >
        <slot name="content">{{ content }}</slot>
      </span>
    </transition>
  </span>
</template>

<style scoped>
.tooltip-enter-active,
.tooltip-leave-active {
  transition: opacity 150ms ease;
}
.tooltip-enter-from,
.tooltip-leave-to {
  opacity: 0;
}
</style>
